import React from "react";
import { Table, Tag, Tooltip } from "antd";

// Standardized status color mapping
const getStatusColor = (status) => {
  const colors = {
    "AVAILABLE": "#87d068",
    "REQUESTED": "#2db7f5", 
    "ISSUED": "#108ee9",
    "DELIVERED": "#f50",
    "completed": "#52c41a",
    "pending": "#faad14",
    "critical": "#f5222d",
    "warning": "#faad14"
  };
  return colors[status] || "#999";
};

const StandardTable = ({
  dataSource = [],
  columns = [],
  loading = false,
  rowKey = "id",
  pagination = false,
  scroll = { y: 200 },
  size = "small",
  bordered = false,
  style = {},
  components = {},
  className = "",
  ...props
}) => {
  // Default table styling
  const defaultStyle = {
    background: 'transparent',
    color: '#fff',
    borderRadius: 12,
    minWidth: 320,
    ...style
  };

  // Default components for consistent styling
  const defaultComponents = {
    body: {
      row: (props) => <tr {...props} style={{ ...props.style, height: 32 }} />,
      cell: (props) => (
        <td 
          {...props} 
          style={{ 
            ...props.style, 
            padding: '4px 8px',
            verticalAlign: 'middle'
          }} 
        />
      ),
    },
    ...components
  };

  return (
    <div
      className={`table-container ${className}`}
      style={{
        height: '100%',
        overflowX: "auto",
        boxSizing: "border-box",
      }}
    >
      <style>
        {`
          .table-container .ant-table-thead > tr > th {
            background: rgba(255,255,255,0.05) !important;
            color: #fff !important;
            font-weight: 600 !important;
            font-size: 13px !important;
            padding: 8px !important;
            border-bottom: 1px solid rgba(255,255,255,0.1) !important;
            position: sticky !important;
            top: 0 !important;
            z-index: 1 !important;
          }
          
          .table-container .ant-table-tbody > tr > td {
            padding: 4px 8px !important;
            vertical-align: middle !important;
          }
          
          .table-container .ant-table-thead > tr > th.ant-table-cell-align-left,
          .table-container .ant-table-tbody > tr > td.ant-table-cell-align-left {
            text-align: left !important;
          }
          
          .table-container .ant-table-thead > tr > th.ant-table-cell-align-center,
          .table-container .ant-table-tbody > tr > td.ant-table-cell-align-center {
            text-align: center !important;
          }
          
          .table-container .ant-table-thead > tr > th.ant-table-cell-align-right,
          .table-container .ant-table-tbody > tr > td.ant-table-cell-align-right {
            text-align: right !important;
          }
        `}
      </style>
      <Table
        dataSource={dataSource}
        columns={columns}
        loading={loading}
        rowKey={rowKey}
        pagination={pagination}
        scroll={scroll}
        size={size}
        bordered={bordered}
        style={defaultStyle}
        components={defaultComponents}
        {...props}
      />
    </div>
  );
};

// Helper function to create status column
export const createStatusColumn = (dataIndex, title = "Status", width = "12%") => ({
  title,
  dataIndex,
  key: dataIndex,
  width,
  render: (status) => (
    <Tag color={getStatusColor(status)} style={{ fontWeight: 500 }}>
      {status}
    </Tag>
  ),
});

// Helper function to create progress column
export const createProgressColumn = (dataIndex, title = "Progress", width = "15%") => ({
  title,
  dataIndex,
  key: dataIndex,
  width,
  render: (text, record) => {
    const completed = record.completedQuantity || 0;
    const remaining = record.remainingQuantity || 0;
    const ordered = record.orderedQuantity || completed + remaining;
    const percentCompleted = ordered > 0 ? (completed / ordered) * 100 : 0;
    const percentRemaining = ordered > 0 ? (remaining / ordered) * 100 : 0;
    
    return (
      <Tooltip
        title={`Completed: ${completed}, Remaining: ${remaining}, Ordered: ${ordered}`}
      >
        <div style={{ width: '100%', margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            height: 20,
            borderRadius: 10,
            overflow: "hidden",
            background: "#f4f6fa",
            boxShadow: "0 2px 8px #0001",
            border: "1px solid #e0e0e0",
            position: "relative",
          }}
        >
          {ordered > 0 ? (
            <>
              {/* Completed Segment */}
              <div
                style={{
                  width: `${percentCompleted}%`,
                  background: "linear-gradient(90deg, #52c41a 60%, #7fffa2 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: percentCompleted > 15 ? "center" : "flex-end",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 11,
                  paddingLeft: 6,
                  paddingRight: percentCompleted > 15 ? 6 : 2,
                  transition: "width 0.3s",
                  position: "relative",
                }}
              >
                {percentCompleted > 10 && (
                  <span style={{ textShadow: "0 1px 2px #0003" }}>
                    {completed}
                  </span>
                )}
              </div>
              {/* Separator if both segments present */}
              {percentCompleted > 0 && percentRemaining > 0 && (
                <div
                  style={{
                    width: 2,
                    background: "#fff",
                    height: "70%",
                    alignSelf: "center",
                    zIndex: 1,
                  }}
                />
              )}
              {/* Remaining Segment */}
              <div
                style={{
                  width: `${percentRemaining}%`,
                  background: "linear-gradient(90deg, #1890ff 60%, #40a9ff 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: percentRemaining > 15 ? "center" : "flex-start",
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: 11,
                  paddingRight: 6,
                  paddingLeft: percentRemaining > 15 ? 6 : 2,
                  transition: "width 0.3s",
                  position: "relative",
                }}
              >
                {percentRemaining > 10 && (
                  <span style={{ textShadow: "0 1px 2px #0003" }}>
                    {remaining}
                  </span>
                )}
              </div>
            </>
          ) : (
            // Empty bar for 0/0
            <div style={{ width: "100%", height: "100%" }} />
          )}
        </div>
        <div
          style={{
            fontSize: 10,
            color: "#888",
            marginTop: 2,
            textAlign: "center",
            letterSpacing: 0.5,
          }}
        >
          {ordered > 0 ? (
            `${completed} / ${ordered}`
          ) : (
            <span style={{ color: "#bbb" }}>0 / 0</span>
          )}
        </div>
      </div>
      </Tooltip>
    );
  },
});

export { getStatusColor };
export default StandardTable; 